import React, { useEffect, useMemo, useState } from 'react'
import ComboBox from 'components/ComboBox/comboBox'
import FormRow, { FormRowProps } from 'components/FormRow'
import useOrdersQuery, { Order } from '../../hooks/useOrdersQuery'

export type OrderPickerProps = {
	/** Selected order ID */
	selectedOrderId?: string,
	/** Callback when order selection changes */
	onChange: ( orderId: string, order?: Order ) => void,
	/** Form row props for validation and styling */
	formRowProps?: Partial<FormRowProps>,
	/** Whether the picker is disabled */
	isDisabled?: boolean,
	/** Placeholder text */
	placeholder?: string,
	/** Prepend element for the combo box */
	prepend?: string | React.ReactElement | ( string | React.ReactElement )[],
	/** Test ID for testing */
	'data-testid'?: string,
	/** Branch ID to filter orders by */
	branchId?: string,
}

export default function OrderPicker( {
	selectedOrderId,
	onChange,
	formRowProps,
	isDisabled,
	placeholder = 'Search for orders...',
	prepend,
	'data-testid': testId,
	branchId,
}: OrderPickerProps ) {
	const { searchOrders, orders, loading } = useOrdersQuery()
	const [ searchString, setSearchString ] = useState( '' )

	// Held as state rather than searched straight from the callback so a branch
	// change re-scopes the current search — `ComboBox` only calls back on input.
	// The empty guard is the pre-conversion effect's: clearing must not search.
	useEffect( () => {
		if ( searchString ) {
			searchOrders( searchString, branchId )
		}
	}, [ searchString, searchOrders, branchId ] )

	// Fetch pre-selected order when ID is provided without a prior search
	useEffect( () => {
		if ( selectedOrderId ) {
			searchOrders( selectedOrderId, branchId )
		}
	}, [ selectedOrderId, searchOrders, branchId ] )

	const selectedOrder = useMemo( () => (
		selectedOrderId
			? orders.find( ( order ) => String( order.order_id ) === selectedOrderId )
			: undefined
	), [ selectedOrderId, orders ] )

	const handleChange = ( order?: Order ) => {
		onChange( order ? String( order.order_id ) : '', order )
	}

	return (
		<FormRow label="Order ID" {...formRowProps}>
			<ComboBox<Order>
				placeholder={placeholder}
				data={orders}
				getName={( order ) => String( order.order_id )}
				selectedOption={selectedOrder}
				setSelectedOption={handleChange}
				setSearchString={setSearchString}
				debounceDelayMs={400}
				// The pre-conversion `EuiComboBox` wasn't async, so it filtered the current
				// results by the typed text while the next search was pending.
				async={false}
				isLoading={loading}
				isDisabled={isDisabled}
				isInvalid={formRowProps?.isInvalid}
				prepend={prepend}
				data-testid={testId}
			/>
		</FormRow>
	)
}
